import React from 'react';
import './FeasibilityModal.css';

// Helper function to format validation keys
const formatValidationKey = (key) => {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
};

// Helper function to determine CSS class based on validation result
const getValidationClass = (value) => {
  if (typeof value !== 'string') return '';
  const lowerValue = value.toLowerCase();
  if (lowerValue.includes('pass') || lowerValue.includes('good')) {
    return 'validation-pass';
  } else if (lowerValue.includes('fail') || lowerValue.includes('poor')) {
    return 'validation-fail';
  } else if (lowerValue.includes('moderate') || lowerValue.includes('medium')) {
    return 'validation-moderate';
  }
  return '';
};

const FeasibilityModal = ({ isOpen, onClose, feasibilityData, isFeasible = false }) => {
  if (!isOpen || !feasibilityData) return null;

  const { 
    score,
    feasibility_score, // API returns this field name
    issues, 
    suggestions, 
    strengths,
    opportunities,
    recommendation, 
    message,
    validation_details,
    feasibility_check 
  } = feasibilityData;
  
  // Use feasibility_score if score is not present
  const finalScore = score || feasibility_score;

  // Merge validation_details and feasibility_check (they're the same data, different field names)
  const validationDetails = validation_details || feasibility_check;

  return (
    <div className="feasibility-modal-overlay" onClick={onClose}>
      <div className={`feasibility-modal ${isFeasible ? 'feasible' : 'not-feasible'}`} onClick={(e) => e.stopPropagation()}>
        <div className="feasibility-modal-header">
          <div className="feasibility-icon">{isFeasible ? '✅' : '❌'}</div>
          <h2>{isFeasible ? 'Idea is Feasible!' : 'Idea Not Feasible'}</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="feasibility-modal-content">
          {/* Message from backend (if available) */}
          {message && (
            <div className="feasibility-message">
              {message}
            </div>
          )}

          <div className="feasibility-score">
            <span className="score-label">Feasibility Score:</span>
            <span className="score-value">{finalScore}/1.0</span>
          </div>

          {/* Validation Details (breakdown by category) */}
          {validationDetails && Object.keys(validationDetails).length > 0 && (
            <div className="feasibility-section">
              <h3>📋 Validation Details</h3>
              <div className="validation-details">
                {Object.entries(validationDetails).map(([key, value]) => (
                  <div key={key} className="validation-item">
                    <strong>{formatValidationKey(key)}:</strong>
                    <span className={getValidationClass(value)}>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Strengths (for passed ideas) */}
          {strengths && strengths.length > 0 && (
            <div className="feasibility-section">
              <h3>💪 Strengths</h3>
              <ul className="strength-list">
                {strengths.map((strength, idx) => (
                  <li key={idx}>{strength}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Opportunities (for passed ideas) */}
          {opportunities && opportunities.length > 0 && (
            <div className="feasibility-section">
              <h3>🎯 Opportunities</h3>
              <ul className="opportunity-list">
                {opportunities.map((opportunity, idx) => (
                  <li key={idx}>{opportunity}</li>
                ))}
              </ul>
            </div>
          )}

          {issues && issues.length > 0 && (
            <div className="feasibility-section">
              <h3>⚠️ Issues Found</h3>
              <ul className="issue-list">
                {issues.map((issue, idx) => (
                  <li key={idx}>{issue}</li>
                ))}
              </ul>
            </div>
          )}

          {suggestions && suggestions.length > 0 && (
            <div className="feasibility-section">
              <h3>💡 Suggestions to Improve</h3>
              <ul className="suggestion-list">
                {suggestions.map((suggestion, idx) => (
                  <li key={idx}>{suggestion}</li>
                ))}
              </ul>
            </div>
          )}

          {recommendation && (
            <div className="feasibility-recommendation">
              <strong>Recommendation:</strong> {recommendation}
            </div>
          )}

          {isFeasible && (
            <div className="feasibility-progress-message">
              ✅ <strong>Feasibility check passed!</strong> Report generation is already in progress.
            </div>
          )}
        </div>

        <div className="feasibility-modal-footer">
          {isFeasible ? (
            <button className="btn-primary" onClick={onClose}>
              OK
            </button>
          ) : (
            <button className="btn-primary" onClick={onClose}>
              Got it, I'll revise my idea
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeasibilityModal;
